import { useEffect, useCallback, useRef } from 'react'
import { useMsal } from '@azure/msal-react'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { encrypt, decrypt } from 'src/utils/storage'
import { STORAGE_KEY } from 'src/constants'

const isTokenExpiringSoon = (token: string, offsetMinutes: number = 10) => {
  try {
    const decoded = JSON.parse(atob(token.split('.')[1]))
    const currentTime = Date.now() / 1000
    const timeUntilExpiry = decoded.exp - currentTime
    const offsetSeconds = offsetMinutes * 60

    return timeUntilExpiry <= offsetSeconds
  } catch (error) {
    console.error('Error checking token expiration:', error)
    return true
  }
}

export const useMsalTokenRefresh = () => {
  const { instance, accounts } = useMsal()
  const lastRefreshRef = useRef<number>(0)

  const refreshToken = useCallback(
    async (force: boolean = false) => {
      if (!accounts[0]) return null

      const now = Date.now()
      if (!force && now - lastRefreshRef.current < 30000) {
        return null
      }

      try {
        const response = await instance.acquireTokenSilent({
          scopes: ['user.read'],
          account: accounts[0],
          forceRefresh: force,
        })

        if (response.idToken) {
          localStorage.setItem(STORAGE_KEY, encrypt(response.idToken))
          lastRefreshRef.current = now
          return response.idToken
        }
        return null
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          try {
            const popupResponse = await instance.acquireTokenPopup({
              scopes: ['user.read'],
            })
            if (popupResponse.idToken) {
              localStorage.setItem(STORAGE_KEY, encrypt(popupResponse.idToken))
              lastRefreshRef.current = now
              return popupResponse.idToken
            }
          } catch (popupError) {
            console.error('MSAL popup error during refresh:', popupError)
          }
        }
        return null
      }
    },
    [instance, accounts]
  )

  const checkAndRefreshIfNeeded = useCallback(async () => {
    const currentToken = localStorage.getItem(STORAGE_KEY)
    if (!currentToken) return

    try {
      const decryptedToken = decrypt(currentToken)
      if (isTokenExpiringSoon(decryptedToken, 15)) {
        await refreshToken(true)
      }
    } catch (error) {
      console.error('Error checking token expiration:', error)
    }
  }, [refreshToken])

  useEffect(() => {
    if (accounts.length > 0) {
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          checkAndRefreshIfNeeded()
        }
      }

      document.addEventListener('visibilitychange', handleVisibilityChange)

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      }
    }
  }, [accounts, checkAndRefreshIfNeeded])

  return { refreshToken, checkAndRefreshIfNeeded }
}
